// Matt: "All of our apps need the new version auto reload feature we have in
// Cinemaroll." This is the safety half of that feature, ported from
// cinemaroll's src/utils/appUpdate.js and re-aimed at a game.
//
// checkForUpdate.js does the DETECTING (is a newer build live?); this file
// answers the only question that actually matters once one is found: is it
// OK to reload right now? Cinemaroll's version of that question was mild -
// worst case someone lost a half-typed search box. Here it's the whole
// feature: yanking the page out from under two people mid-dungeon-run is
// worse than showing them a stale build for another ten minutes.
//
// What a reload here actually costs (traced through store/index.js before
// writing this, because "it's persisted, so it's fine" turned out to be
// only two-thirds true):
//
//   - `gameState` itself DOES survive. Every submitAction persists the full
//     state - localStorage for local hotseat/solo play (localGameStorage.js),
//     Firebase for remote - and hash-history routing means the reload lands
//     back on the same route. So the board comes back.
//   - `undoStack` does NOT. store/index.js calls it out explicitly: "Purely
//     local UI state - deliberately never synced." It isn't written to
//     localStorage either. Reload mid-turn and every not-yet-locked-in action
//     you took this turn becomes permanent.
//   - A TUTORIAL game does not survive at all. startTutorial deliberately
//     skips writeLocalGame ("a tutorial run is disposable"), so gameState was
//     never written anywhere - a reload drops the whole run on the floor.
//   - An AI turn in flight does not survive. submitAction's `while
//     (!gameOver)` loop persists after each AI turn but is otherwise pure
//     in-memory, and nothing resumes it on boot (loadLocalGame just commits
//     what it read). Reload between two AI turns and the game comes back with
//     an AI as the active player and nothing left running to advance it.
//
// So: a game in progress is never a safe moment, not even after 25 seconds of
// stillness. Players think between turns - stillness is the normal state of a
// board game, not evidence that nothing is happening. Landing, the lobby, and
// a finished game are the safe moments.
import { waitForNewWorker } from './registerServiceWorker';

// Every overlay in this app - the bug-report form, the glossary popup, the
// error modal, the install sheet, the tutorial spotlight, and all eleven
// in-game choice panels - roots itself in a class ending in `-backdrop`
// (grep for it; the convention is unbroken). One selector covers the lot,
// including any panel added later that follows the same shape.
const OVERLAY_SELECTOR = '[class*="backdrop"]';

const TEXT_ENTRY_TAGS = /^(INPUT|TEXTAREA|SELECT)$/;

// One auto-reload attempt per detected build, ever. If the reload somehow
// doesn't land us on the new version (a stuck worker, a cache oddity), the
// app stays on the old one rather than reloading in a loop - which on a game
// screen would be indistinguishable from a crash.
const ATTEMPT_KEY = 'thunderstoner:autoUpdateAttemptedFor';

/**
 * Is RIGHT NOW a safe moment to reload out from under the user?
 *
 * Every input is injectable so this stays a plain, directly-testable
 * function rather than something you can only exercise through a mounted
 * component. See the file header for why each rule is here.
 */
export function isSafeMomentForReload({
  activeElement = typeof document !== 'undefined' ? document.activeElement : null,
  root = typeof document !== 'undefined' ? document : null,
  routePath = '',
  gameState = null,
  tutorialActive = false,
} = {}) {
  // They're typing - the room code they're halfway through entering, or a
  // bug report they're in the middle of writing.
  const tag = activeElement?.tagName || '';
  if (TEXT_ENTRY_TAGS.test(tag)) return false;
  if (activeElement?.isContentEditable) return false;

  // Any overlay open means they're mid-flow in something.
  if (root?.querySelector?.(OVERLAY_SELECTOR)) return false;

  // A tutorial run is pure in-memory state - a reload ends it, wherever
  // they are in it.
  if (tutorialActive) return false;

  // Off the board (landing, /new, /join) there is nothing in flight to
  // lose: a saved local game is in localStorage and comes back on boot.
  if (!isGameRoute(routePath)) return true;

  // On the board, the only safe moment is a game that's already over.
  // Note the shape of this: unknown/still-loading gameState reads as unsafe,
  // which is the direction we want to fail in.
  return Boolean(gameState?.meta?.gameOver);
}

// `/game` (local hotseat/solo) and `/game/:gameId` (remote) - see
// router/index.js. `/new` and `/join/:code` are lobby screens, not the board.
export function isGameRoute(routePath) {
  return typeof routePath === 'string' && (routePath === '/game' || routePath.startsWith('/game/'));
}

/**
 * Has this session already tried to auto-reload onto this exact build?
 * Records the attempt as a side effect, so the caller only ever gets one
 * `true` per target. sessionStorage (not local) on purpose: a genuinely new
 * tab deserves a fresh attempt.
 */
export function shouldAutoAttempt(target, storage = typeof window !== 'undefined' ? window.sessionStorage : null) {
  try {
    if (!storage) return true;
    if (storage.getItem(ATTEMPT_KEY) === target) return false;
    storage.setItem(ATTEMPT_KEY, target);
    return true;
  } catch {
    // Storage disabled (Safari private browsing, quota) - better to try
    // once than never. Same best-effort stance as localGameStorage.js.
    return true;
  }
}

/**
 * The reload itself. Waits for any in-flight service-worker install to settle
 * first so we land on the NEW app rather than a half-old/half-new mix, then
 * reloads. Every failure inside waitForNewWorker is swallowed there - nothing
 * is allowed to eat the reload.
 */
export async function reloadForUpdate() {
  await waitForNewWorker();
  window.location.reload();
}
